import type { ReactNode } from 'react';
import { Alert, Button, Chip, Grid, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { CustomerOperations as Operations, OperationDevice } from '@/features/customers/types';
import {
  commercialModalityLabels,
  formatHistoryTimestamp,
  formatQuotationDate,
  formatSaleDate,
  paymentMethodLabels,
} from '@/features/operations/labels';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';
import { StatusChip } from '@/components/StatusChip';

export function describeOperationDevice(device: OperationDevice): string {
  return `${device.brand} ${device.model}${
    device.storageGb === null ? '' : ` · ${device.storageGb}GB`
  }`;
}

/// A sale from before the panel asked how it was paid has no method on file.
export function getPaymentMethodLabel(paymentMethod: string | null): string {
  return paymentMethod
    ? (paymentMethodLabels[paymentMethod] ?? paymentMethod)
    : 'Medio no informado';
}

function SummaryFigure({ label, value }: { label: string; value: number }) {
  return (
    <Grid item xs={6} md={3}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" component="p" sx={{ mt: 0.5 }}>
        {value}
      </Typography>
    </Grid>
  );
}

/// The four figures that open the sheet (#230). Without them loaded, nothing takes their place: the
/// section below says what happened.
export function CustomerSummary({ summary }: { summary: Operations['summary'] }) {
  return (
    <Grid container spacing={3} data-section="summary">
      <SummaryFigure label="Cotizaciones" value={summary.quotations} />
      <SummaryFigure label="Equipos vendidos a PhoneTec" value={summary.soldDevices} />
      <SummaryFigure label="Compras" value={summary.purchases} />
      <SummaryFigure label="Reservas activas" value={summary.activeReservations} />
    </Grid>
  );
}

/// What it is on the left, the way in on the right, like the quotations of the same sheet.
function OperationRow({ device, children }: { device: OperationDevice; children: ReactNode }) {
  const phone = describeOperationDevice(device);

  return (
    <Stack
      component="li"
      direction="row"
      spacing={1.5}
      alignItems="center"
      justifyContent="space-between"
    >
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
          {phone}
        </Typography>
        {children}
      </Stack>
      <Button
        component={RouterLink}
        to={`/admin/devices/${device.id}`}
        size="small"
        variant="outlined"
        aria-label={`Ver equipo ${phone}`}
        sx={{ flexShrink: 0 }}
      >
        Ver equipo
      </Button>
    </Stack>
  );
}

function OperationGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" component="h6">
        {title}
      </Typography>
      <Stack component="ul" spacing={1.5} sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {children}
      </Stack>
    </Stack>
  );
}

function RowDetail({ children }: { children: ReactNode }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
      {children}
    </Typography>
  );
}

function RowChips({ children }: { children: ReactNode }) {
  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
      {children}
    </Stack>
  );
}

const expiryVerbs: Record<Operations['reservations'][number]['status'], string> = {
  ACTIVE: 'Vence',
  EXPIRED: 'Venció',
  CANCELLED: 'Vencía',
  COMPLETED: 'Vencía',
};

/**
 * What the customer bought, reserved and sold to PhoneTec, on one card (CRM-6, #230): whoever gave
 * a phone in part payment and bought another sees both together (P-E5). Only to read: each
 * operation is changed on its device.
 */
export function CustomerOperations({
  operations,
  isLoading,
  error,
  onRetry,
}: {
  operations: Operations | null;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  const { formatNullablePrice } = usePriceDisplay();

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Reintentar
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (isLoading || !operations) {
    return (
      <Typography variant="body2" color="text.secondary">
        Cargando las operaciones…
      </Typography>
    );
  }

  const { purchases, reservations, soldDevices } = operations;

  if (!purchases.length && !reservations.length && !soldDevices.length) {
    return (
      <Alert severity="info">
        Todavía no vendió, reservó ni compró. Lo que haga con su cuenta, en la web o en el
        mostrador, va a aparecer acá.
      </Alert>
    );
  }

  return (
    <Stack spacing={3} data-section="operations">
      {purchases.length ? (
        <OperationGroup title="Compras">
          {purchases.map((purchase) => (
            <OperationRow key={purchase.id} device={purchase.device}>
              {purchase.revertedAt ? (
                <RowChips>
                  <StatusChip domain="sale" status="REVERTED" />
                </RowChips>
              ) : null}
              <RowDetail>
                {formatSaleDate(purchase.soldAt, purchase.paymentMethod)} ·{' '}
                {formatNullablePrice(purchase.finalPriceMinor)} ·{' '}
                {getPaymentMethodLabel(purchase.paymentMethod)}
              </RowDetail>
              {purchase.revertedAt ? (
                <RowDetail>
                  {/* The time already ends in "p. m.": a separator, not a period. */}
                  Revertida el {formatHistoryTimestamp(purchase.revertedAt)} · Motivo:{' '}
                  {purchase.reversalReason}
                </RowDetail>
              ) : (
                <RowDetail>
                  {purchase.warrantyUntil
                    ? `Garantía hasta el ${formatSaleDate(purchase.warrantyUntil, purchase.paymentMethod)}`
                    : 'Garantía no informada'}
                </RowDetail>
              )}
            </OperationRow>
          ))}
        </OperationGroup>
      ) : null}

      {reservations.length ? (
        <OperationGroup title="Reservas">
          {reservations.map((reservation) => (
            <OperationRow key={reservation.id} device={reservation.device}>
              <RowChips>
                <StatusChip domain="reservation" status={reservation.status} />
              </RowChips>
              <RowDetail>
                {expiryVerbs[reservation.status]} el {formatHistoryTimestamp(reservation.expiresAt)}
              </RowDetail>
            </OperationRow>
          ))}
        </OperationGroup>
      ) : null}

      {soldDevices.length ? (
        <OperationGroup title="Equipos que vendió a PhoneTec">
          {soldDevices.map(({ device, deviceState, commercialModality, receivedAt }) => (
            <OperationRow key={device.id} device={device}>
              <RowChips>
                {commercialModality ? (
                  <Chip
                    size="small"
                    color="secondary"
                    label={commercialModalityLabels[commercialModality] ?? commercialModality}
                  />
                ) : null}
                <StatusChip domain="device" status={deviceState} />
              </RowChips>
              <RowDetail>Recibido el {formatQuotationDate(receivedAt)}</RowDetail>
            </OperationRow>
          ))}
        </OperationGroup>
      ) : null}
    </Stack>
  );
}
